import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { useAuth } from '@clerk/clerk-react';
import { useAuthContext } from '@/contexts/AuthContext';
import { ActivitiesService } from '@/api';
import type { ActivityListParams } from '@/types/activities';

// Query keys for activities
export const activityKeys = {
  all: ['activities'] as const,
  lists: () => [...activityKeys.all, 'list'] as const,
  list: (params: ActivityListParams) => [...activityKeys.lists(), params] as const,
  details: () => [...activityKeys.all, 'detail'] as const,
  detail: (id: string) => [...activityKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated activities with filters.
 *
 * @param params - Filter parameters for activities query
 * @returns TanStack Query result with activity list data
 *
 * @example
 * const { data, isLoading, error } = useActivities({
 *   page: 1,
 *   page_size: 20,
 *   child_id: 'abc123',
 *   type: 'check_in',
 *   date_from: new Date('2025-01-01'),
 *   date_to: new Date('2025-01-31'),
 * });
 */
export function useActivities(params: ActivityListParams = {}) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  // Convert Date objects to ISO strings for API
  const apiParams = {
    page: params.page ?? 1,
    pageSize: params.page_size ?? 20,
    childId: params.child_id,
    type: params.type as Parameters<typeof ActivitiesService.getApiV1Activities>[3],
    dateFrom: params.date_from?.toISOString(),
    dateTo: params.date_to?.toISOString(),
    groupId: params.group_id,
    childcareId: params.childcare_id,
  };

  return useQuery({
    queryKey: activityKeys.list(params),
    queryFn: () =>
      ActivitiesService.getApiV1Activities(
        apiParams.page,
        apiParams.pageSize,
        apiParams.childId,
        apiParams.type,
        apiParams.dateFrom,
        apiParams.dateTo,
        apiParams.groupId,
        apiParams.childcareId
      ),
    enabled: isSignedIn && isAuthReady,
    // Keep the previous page's rows on screen while the next page loads so the
    // table does not flash empty when paging/filtering (gate derived math on
    // isPlaceholderData at the call site).
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on 401/403 errors
      if (
        error instanceof Error &&
        (error.message.includes('401') ||
          error.message.includes('403') ||
          error.message.includes('Unauthorized'))
      ) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to fetch a single activity by ID.
 *
 * @param id - Activity ID
 * @param options - Optional query options
 * @returns TanStack Query result with activity data
 */
export function useActivity(id: string, options?: { enabled?: boolean }) {
  const { isSignedIn } = useAuth();
  const { isAuthReady } = useAuthContext();

  return useQuery({
    queryKey: activityKeys.detail(id),
    queryFn: () => ActivitiesService.getApiV1Activities1(id),
    enabled: (options?.enabled ?? true) && !!id && isSignedIn && isAuthReady,
    staleTime: 30000, // 30 seconds
  });
}
